// ============================================================================
// OMS sales-order list — paginated Ant table. Row click opens the drill-down.
// ============================================================================
import {
  CopyOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  RedoOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Popconfirm,
  Popover,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { InputRef } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  OmsPipelineCounts,
  OmsQueue,
  OmsStage,
  listSalesOrders,
  retrySalesOrders,
} from "../oms.api";
import { SalesOrderListRow } from "../types";
import { DeliveryStatusTag } from "../logistics/components/logistics_status_tag";
import { useIsAdmin } from "../../../custom_hooks/use_is_admin";
import { useRememberedTab } from "../remembered_tab";
import CopyText from "./copy_text";
import ExpressTag from "./express_tag";
import PipelineBoard, { FilterSummary, QUEUES, STAGE_BY_KEY, lateRowClass } from "./pipeline_board";
import StatusTag from "./status_tag";
import UnloadingTag from "./unloading_tag";

const PAGE_SIZE = 20;

// Orders "Retry all" leaves alone — nothing on a closed order can be requeued. The backend skips
// them too; filtering here as well keeps the button's own count honest.
const DONE_STATUSES = ["completed", "cancelled"];

const ALL = "all";
const NONE = "none";
type StageKey = OmsStage | typeof ALL;
type QueueKey = OmsQueue | typeof NONE;
const STAGE_KEYS: StageKey[] = [ALL, ...(Object.keys(STAGE_BY_KEY) as OmsStage[])];
const QUEUE_KEYS: QueueKey[] = [NONE, ...QUEUES.map((q) => q.key)];

type SortKey = "newest" | "promised";

const COLUMN_STORAGE_KEY = "oms.overview.hiddenColumns.v2";
const OPTIONAL_COLUMNS: { key: string; label: string }[] = [
  { key: "client", label: "Client" },
  { key: "promised", label: "Promised date" },
  { key: "oms_status", label: "OMS status" },
  { key: "delivery", label: "Delivery" },
  { key: "total", label: "Total" },
  { key: "assigned", label: "Assigned to" },
  { key: "placed", label: "Order placed" },
  { key: "order_status", label: "Sales status" },
];
const DEFAULT_HIDDEN: string[] = [];

const readHidden = (): string[] => {
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : DEFAULT_HIDDEN;
  } catch {
    return DEFAULT_HIDDEN;
  }
};

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

// Column header with a hover explanation — the two status columns are otherwise
// indistinguishable to anyone who doesn't already know the data model.
const headerWithHelp = (label: string, help: string) => (
  <Tooltip title={help}>
    <span>
      {label} <InfoCircleOutlined style={{ color: "#bbb", fontSize: 12 }} />
    </span>
  </Tooltip>
);

// Open an order in a NEW browser tab. The dashboard is a work queue ops scans top-to-bottom, so
// opening the detail in place (and losing the scroll position / page / filters on the way back) is
// the wrong default — every way into an order opens a new tab instead.
const orderHref = (id: number) => `/oms/${id}`;
const openOrder = (id: number) => window.open(orderHref(id), "_blank", "noopener,noreferrer");

interface Props {
  // Optional selection hook (kept for API compatibility); the row itself opens a new tab.
  onSelect?: (salesOrderId: number) => void;
  selectedId?: number;
}

const SalesOrderList: React.FC<Props> = ({ selectedId }) => {
  const [rows, setRows] = useState<SalesOrderListRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [stage, setStage] = useRememberedTab<StageKey>("oms.overview.stage", STAGE_KEYS, ALL);
  const [queue, setQueue] = useRememberedTab<QueueKey>("oms.overview.queue", QUEUE_KEYS, NONE);
  const [sort, setSort] = useState<SortKey>(() =>
    searchParams.get("sort") === "promised" ? "promised" : "newest"
  );
  const [hidden, setHidden] = useState<string[]>(readHidden);
  useEffect(() => {
    try {
      window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(hidden));
    } catch {
      /* per-browser convenience only */
    }
  }, [hidden]);
  const [counts, setCounts] = useState<OmsPipelineCounts | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState(() => searchParams.get("q") || "");
  const [search, setSearch] = useState(() => (searchParams.get("q") || "").trim());
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Strict admin ROLE — "Retry all" touches every order on screen, so it is deliberately narrower
  // than the per-order Retry (oms.retry, which tech holds too).
  const isAdmin = useIsAdmin();
  const searchRef = useRef<InputRef>(null);
  useEffect(() => {
    searchRef.current?.focus();
    const urlStage = searchParams.get("stage") as StageKey | null;
    const urlQueue = searchParams.get("queue") as QueueKey | null;
    if (urlStage && STAGE_KEYS.includes(urlStage)) setStage(urlStage);
    if (urlQueue && QUEUE_KEYS.includes(urlQueue)) setQueue(urlQueue);
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (e.key === "/" && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const searching = !!search;

  useEffect(() => {
    const next = new URLSearchParams();
    if (stage !== ALL) next.set("stage", stage);
    if (queue !== NONE) next.set("queue", queue);
    if (sort !== "newest") next.set("sort", sort);
    if (search) next.set("q", search);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, queue, sort, search]);

  // Type-to-search. Without this the only way to reach an order is paging through
  // 50-row screens, which is the first thing ops hits on day one.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch((prev) => {
        const next = searchInput.trim();
        if (next !== prev) setPage(1);
        return next;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    // A search reaches every order whatever its stage; the cards keep their unscoped counts.
    const res = await listSalesOrders({
      stage: searching || stage === ALL ? undefined : stage,
      queue: searching || queue === NONE ? undefined : queue,
      sort: sort === "promised" ? "promised" : undefined,
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    });
    if (res) {
      setRows(res.results);
      setCount(res.count);
      if (!searching) setCounts(res.pipeline_counts ?? null);
      setUpdatedAt(Date.now());
    }
    setLoading(false);
  }, [stage, queue, sort, searching, search, page]);

  const activeStage = stage === ALL ? null : stage;
  const activeQueue = queue === NONE ? null : queue;
  const pickStage = (next: StageKey) => {
    setPage(1);
    setStage(next);
  };
  const pickQueue = (next: QueueKey) => {
    setPage(1);
    setQueue(next);
    if (next === "overdue") setSort("promised");
  };
  const clearSearch = () => {
    setPage(1);
    setSearchInput("");
    setSearch("");
  };

  useEffect(() => {
    load();
  }, [load]);

  // The unfinished orders on THIS page — the button's scope is what the operator can see.
  const retryableIds = rows.filter((r) => !DONE_STATUSES.includes(r.status)).map((r) => r.id);

  const onRetryAll = async () => {
    setRetrying(true);
    try {
      await retrySalesOrders(retryableIds);
      await load();
    } finally {
      setRetrying(false);
    }
  };

  const columns: ColumnsType<SalesOrderListRow> = [
    {
      // SO number and lead id were two columns holding the same string on most orders — the SO is
      // raised straight off the enquiry, so the pair only diverges when Zoho hasn't numbered the
      // order yet. One column now: the clickable id, with the lead id (and its tags) underneath
      // only when it actually differs.
      title: "Order",
      key: "so_lead",
      dataIndex: "zoho_salesorder_number",
      width: 230,
      fixed: "left",
      render: (num, row) => {
        const so = (num || "").toString().trim();
        const lead = (row.lead_id || "").toString().trim();
        const primary = so || `SO #${row.id}`;
        const showLead = !!lead && lead !== primary;
        const hasTags = row.mode === "express" || !!row.is_unloading;
        return (
          <Space direction="vertical" size={2} style={{ display: "flex" }}>
            <Space size={4} wrap>
              {/* A real anchor opening a new tab, so native right-click → "open in new tab" works
                  and a plain click doesn't double-fire with the row handler (both open the same
                  new tab anyway). */}
              <Link
                to={orderHref(row.id)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <strong>{primary}</strong>
              </Link>
              {/* Click-to-copy: the lead id is the handle operators paste into Zoho, WhatsApp and
                  search boxes all day, and retyping it off the screen is where transcription
                  errors come from. With no line of its own it rides beside the link as an icon. */}
              {!showLead && lead ? (
                <CopyText value={lead} label="Lead ID" style={{ color: "#bbb" }}>
                  <CopyOutlined />
                </CopyText>
              ) : null}
            </Space>
            {showLead || hasTags ? (
              <Space size={4} wrap>
                {showLead ? (
                  <CopyText value={lead} label="Lead ID">
                    <span style={{ color: "#666" }}>{lead}</span>
                  </CopyText>
                ) : null}
                <ExpressTag show={row.mode === "express"} />
                <UnloadingTag show={row.is_unloading} />
              </Space>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: "Client",
      key: "client",
      dataIndex: "client_name",
      width: 150,
      render: (v, row) => {
        if (!v) return "—";
        if (!row.client_id) return v;
        return (
          <Link
            to={`/client/${row.client_id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {v}
          </Link>
        );
      },
    },
    {
      title: headerWithHelp(
        "Promised date",
        "The earliest date we promised for anything not delivered yet. Click to sort most overdue first."
      ),
      key: "promised",
      dataIndex: "target_delivery_date",
      width: 140,
      sorter: true,
      sortOrder: sort === "promised" ? "ascend" : null,
      sortDirections: ["ascend"],
      render: (v: string | null | undefined) => {
        if (!v) return <span style={{ color: "#bbb" }}>—</span>;
        const late = dayjs().startOf("day").diff(dayjs(v), "day");
        return (
          <Space direction="vertical" size={0}>
            <span>{dayjs(v).format("DD MMM")}</span>
            {late > 0 ? <Tag color="magenta">{late} day{late === 1 ? "" : "s"} late</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: headerWithHelp(
        "OMS status",
        "How far we are with actually fulfilling this order — this is what OMS is tracking."
      ),
      key: "oms_status",
      dataIndex: "status",
      width: 140,
      render: (s, row) => (
        <StatusTag
          status={s}
          tooltip={row.error_message || row.hold_reason}
          errorKind={row.error_kind}
        />
      ),
    },
    {
      // Delivery progress on its own axis — where the goods physically are, independent of any
      // payment/invoice hold on the order. Rolled up across the order's legs (the least-advanced).
      title: headerWithHelp(
        "Delivery",
        "Where the physical delivery stands, independent of payment/invoicing. Rolled up across the order's legs — it reads 'delivered' only once every leg is."
      ),
      key: "delivery",
      dataIndex: "delivery_status",
      width: 130,
      render: (v) => <DeliveryStatusTag status={v} />,
    },
    {
      title: "Total",
      key: "total",
      dataIndex: "total_estimate_amount",
      width: 120,
      align: "right",
      render: (v) => (v != null ? inr.format(Number(v)) : "—"),
    },
    { title: "Assigned to", key: "assigned", dataIndex: "assigned_to", width: 130, render: (v) => v || "—" },
    {
      title: "Order placed",
      key: "placed",
      dataIndex: "order_placed_time",
      width: 130,
      render: (v) =>
        v ? (
          <Tooltip title={dayjs(v).format("DD MMM YYYY HH:mm")}>
            <span>{dayjs(v).format("DD MMM YYYY")}</span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: headerWithHelp(
        "Sales status",
        "The state of the estimate / order — set by sales, not by fulfilment."
      ),
      key: "order_status",
      dataIndex: "estimate_status",
      width: 160,
      render: (v) => {
        if (!v) return "—";
        const t = String(v).replace(/_/g, " ");
        return t.charAt(0).toUpperCase() + t.slice(1);
      },
    },
    {
      title: "Zoho",
      key: "zoho",
      width: 90,
      fixed: "right",
      align: "center",
      render: (_, row) =>
        row.zoho_url ? (
          <Button
            type="link"
            size="small"
            icon={<ExportOutlined />}
            href={row.zoho_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
  ];
  const visibleColumns = columns.filter((c) => !hidden.includes(String(c.key)));

  const columnPicker = (
    <Popover
      trigger="click"
      placement="bottomRight"
      title="Show columns"
      content={
        <Checkbox.Group
          style={{ display: "flex", flexDirection: "column", gap: 6 }}
          value={OPTIONAL_COLUMNS.map((c) => c.key).filter((k) => !hidden.includes(k))}
          onChange={(shown) =>
            setHidden(OPTIONAL_COLUMNS.map((c) => c.key).filter((k) => !shown.includes(k)))
          }
          options={OPTIONAL_COLUMNS.map((c) => ({ label: c.label, value: c.key }))}
        />
      }
    >
      <Button icon={<SettingOutlined />}>Columns</Button>
    </Popover>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Input.Search
          ref={searchRef}
          size="large"
          placeholder="Search any order — SO number, ENQ or client phone  ( / )"
          allowClear
          style={{ maxWidth: 460, flex: "1 1 280px" }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={(v) => {
            setPage(1);
            setSearch(v.trim());
          }}
        />
        <Space wrap>
          {updatedAt ? (
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
              Updated {dayjs(updatedAt).format("HH:mm")}
            </span>
          ) : null}
          {columnPicker}
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          {isAdmin ? (
            <Popconfirm
              title="Retry all on this page"
              description={`Requeue every failed document and booking on the ${retryableIds.length} unfinished order(s) shown. Completed and cancelled orders are skipped.`}
              okText="Retry all"
              onConfirm={onRetryAll}
              disabled={retrying || !retryableIds.length}
            >
              <Button
                icon={<RedoOutlined />}
                loading={retrying}
                disabled={!retryableIds.length}
              >
                Retry all ({retryableIds.length})
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      </div>
      <PipelineBoard
        counts={counts}
        stage={activeStage}
        queue={activeQueue}
        onStage={(next) => pickStage(next ?? ALL)}
        onQueue={(next) => pickQueue(next ?? NONE)}
        dimmed={searching}
      />
      <FilterSummary
        stage={activeStage}
        queue={activeQueue}
        search={search}
        count={count}
        page={page}
        pageSize={PAGE_SIZE}
        loading={loading}
        onStage={() => pickStage(ALL)}
        onQueue={() => pickQueue(NONE)}
        onClearSearch={clearSearch}
      />
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={visibleColumns}
        dataSource={rows}
        onChange={(_, __, sorter, extra) => {
          if (extra.action !== "sort") return;
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          setPage(1);
          setSort(s?.columnKey === "promised" && s.order ? "promised" : "newest");
        }}
        scroll={{ x: "max-content" }}
        sticky
        rowClassName={(r) =>
          r.target_delivery_date && dayjs(r.target_delivery_date).isBefore(dayjs(), "day")
            ? lateRowClass
            : ""
        }
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                search
                  ? `No order matches “${search}”`
                  : activeQueue || activeStage
                    ? "Nothing here — all clear"
                    : "No orders"
              }
            />
          ),
        }}
        onRow={(record) => ({
          // Left-click and middle-click both open the order in a new tab, so the dashboard keeps its
          // place. (The id cells are real links too, so native right-click → "open in new tab" works.)
          onClick: () => openOrder(record.id),
          onAuxClick: (e) => {
            if (e.button === 1) openOrder(record.id);
          },
          style: {
            cursor: "pointer",
            background: record.id === selectedId ? "#e6f7f9" : undefined,
          },
        })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: count,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </Space>
  );
};

export default SalesOrderList;
